import { useAppSelector } from 'hooks/redux';
import React, { Fragment, useMemo } from 'react';

import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { makeStyles } from 'tss-react/mui';

import { taxGroupCategoryNames } from 'app-components/flux/business/businessTypes';
import type { BusinessTaxInfoType } from 'app-components/flux/categories/categoriesTypes';
import { UsageTypeEnum } from 'app-components/flux/categories/categoriesTypes';
import { useField, useMultiFields } from 'app-components/flux/categories/categoryFormHooks';
import { taxFormsSelectors } from 'app-components/flux/tax-forms';
import { taxLineItemsSelectors } from 'app-components/flux/tax-line-items';

import InfoTooltipIcon from 'components/InfoTooltipIcon';
import TaxNavButton from './TaxNavButton';

import { initialCaps } from './utils';

const BUSINESS_TAX_MULTI_FIELD_KEYS = [
  'BUSINESS',
  'BUSINESS_S_CORP',
  'FARMING',
  'RENTAL_PROPERTY',
  'PARTNERSHIP',
] as const;

export type BusinessTaxSummaryRow = {
  groupKey: (typeof BUSINESS_TAX_MULTI_FIELD_KEYS)[number];
  groupLabel: string;
  formName: string;
  lineName: string;
  lineDescription: string;
};

type Props = {
  onEdit: () => void;
  rows: BusinessTaxSummaryRow[];
};

export const useBusinessTaxSummaryRows = (): BusinessTaxSummaryRow[] => {
  const { value: usageType } = useField('usageType');
  const { value: businessTaxBusiness } = useMultiFields('businessTaxInfo', 'BUSINESS');
  const { value: businessTaxBusinessSCorp } = useMultiFields('businessTaxInfo', 'BUSINESS_S_CORP');
  const { value: businessTaxFarming } = useMultiFields('businessTaxInfo', 'FARMING');
  const { value: businessTaxRentalProperty } = useMultiFields('businessTaxInfo', 'RENTAL_PROPERTY');
  const { value: businessTaxPartnership } = useMultiFields('businessTaxInfo', 'PARTNERSHIP');

  const taxFormsById = useAppSelector((s) => taxFormsSelectors.getResourcesById(s));
  const taxLineItemsById = useAppSelector((s) => taxLineItemsSelectors.getResourcesById(s));

  return useMemo(() => {
    if (usageType !== UsageTypeEnum.BUSINESS) {
      return [];
    }

    const businessTaxInfoByKey = {
      BUSINESS: businessTaxBusiness,
      BUSINESS_S_CORP: businessTaxBusinessSCorp,
      FARMING: businessTaxFarming,
      RENTAL_PROPERTY: businessTaxRentalProperty,
      PARTNERSHIP: businessTaxPartnership,
    } satisfies Record<(typeof BUSINESS_TAX_MULTI_FIELD_KEYS)[number], BusinessTaxInfoType>;

    return BUSINESS_TAX_MULTI_FIELD_KEYS.flatMap((key) => {
      const v = businessTaxInfoByKey[key];
      if (!v?.enabled || !v.taxForm || !v.taxLineItem) {
        return [];
      }

      const form = taxFormsById.get(v.taxForm);
      const line = taxLineItemsById.get(v.taxLineItem);
      const groupLabel =
        key in taxGroupCategoryNames
          ? taxGroupCategoryNames[key as keyof typeof taxGroupCategoryNames]
          : initialCaps(key);

      return [
        {
          groupKey: key,
          groupLabel,
          formName: form?.get?.('name') ?? '',
          lineName: line?.get?.('name') ?? '',
          lineDescription: line?.get?.('description') ?? '',
        },
      ];
    });
  }, [
    businessTaxBusiness,
    businessTaxBusinessSCorp,
    businessTaxFarming,
    businessTaxPartnership,
    businessTaxRentalProperty,
    taxFormsById,
    taxLineItemsById,
    usageType,
  ]);
};

const useStyles = makeStyles()((theme) => ({
  taxInfoCard: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.light}`,
    borderRadius: theme.spacing(1.5),
    boxShadow: theme.shadows[2],
    padding: theme.spacing(2),
  },
  taxInfoHeading: {
    flex: '1 1 auto',
    lineHeight: '20px',
    color: theme.palette.text.primary,
    minWidth: 0,
  },
  rowsStack: {
    paddingTop: theme.spacing(2),
  },
  groupColumn: {
    width: 147,
    flexShrink: 0,
    maxWidth: '32%',
    [theme.breakpoints.up('sm')]: {
      maxWidth: 147,
    },
  },
  formColumn: {
    width: 99,
    flexShrink: 0,
  },
  taxLineColumn: {
    flex: '1 1 0',
    minWidth: 0,
  },
  captionText: {
    lineHeight: '16px',
  },
  bodyText: {
    lineHeight: '20px',
    wordBreak: 'break-word',
  },
  taxLineRow: {
    minWidth: 0,
  },
  tooltipBox: {
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    color: theme.palette.text.secondary,
    '& .MuiSvgIcon-root': { fontSize: 16 },
  },
  divider: {
    borderColor: theme.palette.border.light,
  },
}));

const BusinessTaxInfoSummary = ({ onEdit, rows }: Props) => {
  const { classes } = useStyles();

  if (rows.length === 0) {
    return null;
  }

  return (
    <Card elevation={0} data-testid='business-tax-info-summary' className={classes.taxInfoCard}>
      <Stack direction='row' alignItems='flex-start' justifyContent='space-between' columnGap={1.5}>
        <Typography component='span' variant='bodyMedium' className={classes.taxInfoHeading}>
          Tax info
        </Typography>
        <TaxNavButton id='business-tax-info-edit' text='Edit' onClick={onEdit} ariaLabel='Edit tax info' />
      </Stack>

      <Stack className={classes.rowsStack} spacing={2}>
        {rows.flatMap((row, index) => {
          const rowStack = (
            <Stack direction='row' alignItems='flex-start' columnGap={5}>
              <Stack spacing={0.5} className={classes.groupColumn}>
                <Typography variant='captionRegular' color='text.secondary' className={classes.captionText}>
                  Group
                </Typography>
                <Typography variant='bodyRegular' className={classes.bodyText}>
                  {row.groupLabel}
                </Typography>
              </Stack>
              <Stack spacing={0.5} className={classes.formColumn}>
                <Typography variant='captionRegular' color='text.secondary' className={classes.captionText}>
                  Form
                </Typography>
                <Typography variant='bodyRegular' className={classes.bodyText}>
                  {row.formName || '—'}
                </Typography>
              </Stack>
              <Stack spacing={0.5} className={classes.taxLineColumn}>
                <Typography variant='captionRegular' color='text.secondary' className={classes.captionText}>
                  Tax line
                </Typography>
                <Stack direction='row' alignItems='center' columnGap={0.5} className={classes.taxLineRow}>
                  {row.lineDescription ? (
                    <div className={classes.tooltipBox}>
                      <InfoTooltipIcon size='small-no-padding' tooltipText={row.lineDescription} />
                    </div>
                  ) : null}
                  <Typography variant='bodyRegular' className={classes.bodyText}>
                    {row.lineName || '—'}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          );

          if (index === 0) {
            return <Fragment key={row.groupKey}>{rowStack}</Fragment>;
          }

          return (
            <Fragment key={row.groupKey}>
              <Divider className={classes.divider} />
              {rowStack}
            </Fragment>
          );
        })}
      </Stack>
    </Card>
  );
};

export default BusinessTaxInfoSummary;
